import styled from "@emotion/styled";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Switch,
  Table,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, useCallback } from "react";
import useTranslation from "shared/helpers/hooks/useTranslation";
import DotsMenu from "shared/ui/DotsMenu";
import TablePaginationActions from "shared/ui/Table/components/TablePAginationActions";
import {
  IAction,
  IFilterOptions,
  rowsPerPageOptions,
} from "shared/ui/Table/constants";
import { ICampaign } from "store/interfaces/campaigns";
import { IPaginated } from "store/interfaces/main";
import { NOT_FOUND_SURVEYS } from "../constants";

type Props = {
  list: IPaginated<ICampaign>;
  actions: any;
  handleChangeState: (id: number, checked: boolean) => void;
  filterOptions: IFilterOptions;
  onChange: () => void;
  isLoading: boolean;
};

const CardContentNoPadding = styled(CardContent)(`
  padding: 20px;
  &:last-child {
    padding-bottom: 20px;
  }
`);

const StyledBox = styled(Box)(`
    display: flex;
    justify-content: space-between;
    align-items: center;
`);

const CampaignCardsList = ({
  list,
  actions,
  handleChangeState,
  filterOptions,
  onChange,
  isLoading,
}: Props) => {
  const filters = filterOptions?.watch("config");
  const t = useTranslation();

  const handleClickAction = useCallback(
    (action: IAction<ICampaign>, row: ICampaign) => {
      action?.onClick(row);
    },
    []
  );

  const handlePageChange = (_: any, pageNumber: number) => {
    filterOptions?.reset({
      ...filterOptions.watch(),
      config: {
        ...filters,
        start: pageNumber * filters?.length,
      },
    });
    onChange?.();
  };

  const handleRowsPerPageChange = (e: any) => {
    const value = e.target.value;
    filterOptions?.reset({
      ...filterOptions.watch(),
      config: {
        ...filters,
        length: value,
      },
    });
    onChange?.();
  };

  if (!list?.displayData?.length) {
    return (
      <Alert sx={{ mt: 2 }} severity="info">
        {NOT_FOUND_SURVEYS}
      </Alert>
    );
  }

  return (
    <Fragment>
      <Grid container spacing={2} sx={{ paddingTop: 4 }}>
        {list?.displayData?.map((item: ICampaign) => {
          return (
            <Grid
              item
              xs={6}
              sm={4}
              md={3}
              key={item.id}
              sx={{ width: "23%", position: "relative" }}
            >
              <Card>
                <CardContentNoPadding>
                  <Box
                    display="flex"
                    minHeight="180px"
                    alignItems={"center"}
                    justifyContent={"center"}
                    borderRadius={"16px"}
                  >
                    <Typography
                      gutterBottom
                      variant="h6"
                      fontWeight={500}
                      component="div"
                      width="85%"
                      textAlign={"center"}
                    >
                      {item.name}
                    </Typography>
                    <Box
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: 20,
                      }}
                    >
                      <DotsMenu<ICampaign>
                        actions={actions(item)}
                        onActionClick={handleClickAction}
                        row={item}
                        isDisabled={isLoading}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent={"space-between"}>
                    <StyledBox>
                      <Typography variant="body2" color="text.secondary">
                        {item.responded || t("no")} {t("responses")}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      ></Typography>
                    </StyledBox>
                    <StyledBox>
                      <Switch
                        color="success"
                        onChange={(e, checked) => {
                          handleChangeState(item.id, checked);
                        }}
                        checked={item.isActive}
                        disabled={isLoading}
                      />
                    </StyledBox>
                  </Box>
                </CardContentNoPadding>
              </Card>
            </Grid>
          );
        })}
        <Table>
          <TableFooter
            sx={{
              "& p": {
                marginBottom: 0,
              },
            }}
          >
            <TableRow>
              <TablePagination
                component="td"
                count={list?.totalRecords}
                rowsPerPage={filters?.length}
                rowsPerPageOptions={rowsPerPageOptions}
                page={filters?.start / filters?.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>
    </Fragment>
  );
};

export default CampaignCardsList;
